import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Trash2, UserMinus, UserPlus, Users as UsersIcon } from 'lucide-react';
import type { AllowlistRow, Role, User } from '@portal/shared';
import { ROLE_RANK, hasAtLeastRole } from '@portal/shared';
import {
  AppShell,
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Input,
  Modal,
  Select,
  Spinner,
  Table,
  type TableColumn,
  useToast,
} from '../../components/index.js';
import { useAuth } from '../../lib/auth-context.js';
import { api, ApiError } from '../../lib/api-client.js';

const ROLE_OPTIONS: Role[] = ['viewer', 'staff', 'admin', 'owner'];

interface PendingInvite {
  hash: string;
  email: string;
  role: Role;
  addedAt: string;
}

export function Users() {
  const { me } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState<User[] | null>(null);
  const [allowlist, setAllowlist] = useState<AllowlistRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingUid, setPendingUid] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('staff');
  const [invitePending, setInvitePending] = useState(false);
  const [pendingRoleHash, setPendingRoleHash] = useState<string | null>(null);

  const [confirm, setConfirm] = useState<
    | { kind: 'removeUser'; user: User }
    | { kind: 'removeInvite'; row: PendingInvite }
    | null
  >(null);
  const [confirmPending, setConfirmPending] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [usersRes, allowRes] = await Promise.all([
        api.get<{ users: User[] }>('/api/users'),
        api.get<{ entries: AllowlistRow[] }>('/api/allowlist'),
      ]);
      setUsers(usersRes.users);
      setAllowlist(allowRes.entries);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const canManage = !!me && hasAtLeastRole(me.role, 'admin');

  // Non-owner admins can't assign or change anyone to 'owner'.
  const assignableRoles = useMemo<Role[]>(
    () => ROLE_OPTIONS.filter((r) => r !== 'owner' || me?.role === 'owner'),
    [me?.role],
  );

  const pendingInvites = useMemo<PendingInvite[]>(() => {
    if (!users || !allowlist) return [];
    const signedUpEmails = new Set(users.map((u) => u.email.toLowerCase()));
    return allowlist
      .filter((row) => !signedUpEmails.has(row.email.toLowerCase()))
      .map((row) => ({ hash: row.hash, email: row.email, role: row.role, addedAt: row.addedAt }));
  }, [users, allowlist]);

  const onChangeRole = async (target: User, newRole: Role) => {
    if (newRole === target.role) return;
    setPendingUid(target.id);
    try {
      await api.patch(`/api/users/${target.id}`, { role: newRole });
      toast({ title: `Role updated to ${newRole}`, variant: 'success' });
      await refresh();
    } catch (err) {
      toast({
        title: 'Role update failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setPendingUid(null);
    }
  };

  const onInvite = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInvitePending(true);
    try {
      await api.post(`/api/allowlist`, { email, role: inviteRole });
      toast({ title: `Invited ${email} as ${inviteRole}`, variant: 'success' });
      setInviteEmail('');
      setInviteRole('staff');
      await refresh();
    } catch (err) {
      toast({
        title: 'Invite failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setInvitePending(false);
    }
  };

  const onChangeInviteRole = async (row: PendingInvite, newRole: Role) => {
    if (newRole === row.role) return;
    setPendingRoleHash(row.hash);
    try {
      await api.patch(`/api/allowlist/${row.hash}`, { role: newRole });
      toast({ title: `Invite role set to ${newRole}`, variant: 'success' });
      await refresh();
    } catch (err) {
      toast({
        title: 'Role update failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setPendingRoleHash(null);
    }
  };

  const onConfirm = async () => {
    if (!confirm) return;
    setConfirmPending(true);
    try {
      if (confirm.kind === 'removeUser') {
        await api.delete(`/api/users/${confirm.user.id}`);
        toast({ title: `Removed ${confirm.user.email}`, variant: 'success' });
      } else {
        await api.delete(`/api/allowlist/${confirm.row.hash}`);
        toast({ title: `Invite removed for ${confirm.row.email}`, variant: 'success' });
      }
      setConfirm(null);
      await refresh();
    } catch (err) {
      toast({
        title: 'Remove failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setConfirmPending(false);
    }
  };

  const columns = useMemo<TableColumn<User>[]>(
    () => [
      {
        id: 'name',
        header: 'User',
        cell: (u) => (
          <div className="flex items-center gap-3">
            <Avatar name={u.name || u.email} size="sm" />
            <div className="min-w-0">
              <div className="text-text-primary truncate">{u.name || '—'}</div>
              <div className="text-sm text-text-secondary truncate">{u.email}</div>
            </div>
          </div>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        cell: (u) => {
          const isSelf = me?.id === u.id;
          const isOwnerTarget = u.role === 'owner';
          const meRank = me ? ROLE_RANK[me.role] : 0;
          const disabled =
            !canManage ||
            (isOwnerTarget && me?.role !== 'owner') ||
            (isSelf && isOwnerTarget) ||
            ROLE_RANK[u.role] > meRank;

          if (disabled) {
            return <Badge>{u.role}</Badge>;
          }
          return (
            <Select
              aria-label={`Role for ${u.email}`}
              value={u.role}
              disabled={pendingUid === u.id}
              onChange={(e) => onChangeRole(u, e.currentTarget.value as Role)}
            >
              {assignableRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: (u) => {
          const isSelf = me?.id === u.id;
          const isOwner = u.role === 'owner';
          if (!canManage || isSelf || isOwner) return null;
          return (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<UserMinus className="size-4" aria-hidden />}
              onClick={() => setConfirm({ kind: 'removeUser', user: u })}
            >
              Remove
            </Button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage, me, pendingUid, assignableRoles],
  );

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-8 py-10 space-y-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Back to dashboard
        </Link>
        <header>
          <h1 className="text-3xl font-bold text-text-primary">Users</h1>
          <p className="mt-2 text-base text-text-secondary">
            Invite people by adding their email to the allowlist. They sign up themselves and join with whichever role you assigned on the invite. Roles: owner ▸ admin ▸ staff ▸ viewer.
          </p>
        </header>

        {canManage && (
          <Card>
            <CardHeader title="Invite by email" description="Adds the email to the allowlist. Send them the portal URL — they sign up themselves." />
            <CardBody>
              <form onSubmit={onInvite} className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[260px]">
                  <Input
                    label="Email"
                    type="email"
                    required
                    autoComplete="off"
                    placeholder="name@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.currentTarget.value)}
                    leftAdornment={<Mail className="size-4" aria-hidden />}
                  />
                </div>
                <div className="min-w-[140px]">
                  <Select
                    label="Role"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.currentTarget.value as Role)}
                  >
                    {assignableRoles.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                </div>
                <Button
                  type="submit"
                  loading={invitePending}
                  leftIcon={<UserPlus className="size-4" aria-hidden />}
                >
                  Invite
                </Button>
              </form>
            </CardBody>
          </Card>
        )}

        {loading && (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        )}

        {!loading && error && (
          <Card>
            <div role="alert" className="p-6 text-danger">
              Could not load users: {error}
            </div>
          </Card>
        )}

        {!loading && users && users.length === 0 && (
          <Card>
            <EmptyState
              icon={UsersIcon}
              title="No users yet"
              description="Run the seed (or invite someone) to get started."
            />
          </Card>
        )}

        {!loading && users && users.length > 0 && (
          <Card>
            <Table columns={columns} rows={users} rowKey={(u) => u.id} />
          </Card>
        )}

        {!loading && pendingInvites.length > 0 && (
          <Card>
            <CardHeader title="Pending invites" description="Allowlisted emails that haven’t signed up yet." />
            <CardBody>
              <ul className="divide-y divide-border-subtle">
                {pendingInvites.map((row) => (
                  <li key={row.hash} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-text-primary truncate">{row.email}</div>
                      <div className="text-sm text-text-secondary">
                        Invited {new Date(row.addedAt).toLocaleDateString()}
                      </div>
                    </div>
                    {canManage ? (
                      <div className="min-w-[120px]">
                        <Select
                          aria-label={`Role for invite ${row.email}`}
                          value={row.role}
                          disabled={pendingRoleHash === row.hash}
                          onChange={(e) => onChangeInviteRole(row, e.currentTarget.value as Role)}
                        >
                          {assignableRoles.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : (
                      <Badge>{row.role}</Badge>
                    )}
                    {canManage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Trash2 className="size-4" aria-hidden />}
                        onClick={() => setConfirm({ kind: 'removeInvite', row })}
                      >
                        Remove
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}
      </div>

      {confirm && (
        <Modal
          open
          onClose={() => (confirmPending ? undefined : setConfirm(null))}
          title={confirm.kind === 'removeUser' ? 'Remove user?' : 'Remove invite?'}
          description={
            confirm.kind === 'removeUser'
              ? `${confirm.user.email} will be permanently removed: their account, role (${confirm.user.role}), and allowlist entry are all deleted. They’ll need a fresh invite + signup to come back. This cannot be undone.`
              : `${confirm.row.email} will no longer be able to sign up. You can re-invite them later.`
          }
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirm(null)} disabled={confirmPending}>
                Cancel
              </Button>
              <Button variant="danger" onClick={onConfirm} loading={confirmPending}>
                {confirm.kind === 'removeUser' ? 'Remove user' : 'Remove invite'}
              </Button>
            </>
          }
        />
      )}
    </AppShell>
  );
}
